import { User } from '@shared-types/common'

interface UserAvatarProps {
  user: User
  size?: 'xs' | 'sm' | 'md' | 'lg'
  showTooltip?: boolean
}

const AVATAR_COLORS = [
  'bg-info',
  'bg-success',
  'bg-warning',
  'bg-error',
  'bg-purple-500',
  'bg-pink-500',
  'bg-indigo-500',
  'bg-cyan-500',
  'bg-teal-500'
]

export default function UserAvatar({ user, size = 'md', showTooltip = true }: UserAvatarProps) {
  // Generate initials from first_name and last_name, or fall back to email
  const getInitials = (): string => {
    if (user.first_name && user.last_name) {
      return `${user.first_name.charAt(0)}${user.last_name.charAt(0)}`.toUpperCase()
    } else if (user.first_name) {
      return user.first_name.charAt(0).toUpperCase()
    } else if (user.last_name) {
      return user.last_name.charAt(0).toUpperCase()
    } else {
      // Fall back to first 2 chars of email
      return user.email.substring(0, 2).toUpperCase()
    }
  }

  // Get consistent color based on user ID (hash the UUID string)
  const getColor = (): string => {
    // Simple hash function for UUID string
    let hash = 0
    for (let i = 0; i < user.id.length; i++) {
      hash = user.id.charCodeAt(i) + ((hash << 5) - hash)
    }
    return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length]
  }

  // Size classes for avatar
  const sizeClass = {
    xs: 'w-6 h-6 text-xs',
    sm: 'w-8 h-8 text-xs',
    md: 'w-10 h-10 text-sm',
    lg: 'w-12 h-12 text-base'
  }[size]

  const displayName = user.first_name && user.last_name
    ? `${user.first_name} ${user.last_name}`
    : user.email

  // If user has an avatar image, show it; otherwise fall back to initials
  if (user.avatar_url) {
    const avatarContent = (
      <div className="avatar">
        <div className={`rounded-full ${sizeClass}`}>
          <img src={user.avatar_url} alt={displayName} />
        </div>
      </div>
    )

    return showTooltip ? (
      <div className="tooltip" data-tip={displayName}>
        {avatarContent}
      </div>
    ) : avatarContent
  }

  // Fall back to initials
  const avatarContent = (
    <div className="avatar placeholder">
      <div className={`${getColor()} text-white rounded-full flex items-center justify-center ${sizeClass}`}>
        <span>{getInitials()}</span>
      </div>
    </div>
  )

  return showTooltip ? (
    <div className="tooltip" data-tip={displayName}>
      {avatarContent}
    </div>
  ) : avatarContent
}
